import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import AdminShell from "./AdminShell";
import { getDataAPIAuth } from "../../utils/API";

const STATUS_COLORS = {
  inactive: { bg: "#f3f4f6", text: "#6b7280", dot: "#9ca3af", label: "Inactive" },
  sandbox:  { bg: "#fef3c7", text: "#92400e", dot: "#f59e0b", label: "Sandbox" },
  active:   { bg: "#d1fae5", text: "#065f46", dot: "#10b981", label: "Active" },
};

const INTEGRATION_META = {
  nafath: {
    title: "Nafath",
    subtitle: "ELM Identity Verification",
    description:
      "Verify Saudi national identities using the Nafath platform by ELM. Required for KYC onboarding of investors and property owners.",
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
        <path d="M16 11l1.5 1.5L21 9" />
      </svg>
    ),
    accentColor: "#0e3725",
    path: "/admin/integrations/nafath",
    provider: "ELM (Electronic Minds)",
    docUrl: "https://nafath.api.elm.sa/",
  },
  rer: {
    title: "RER",
    subtitle: "Real Estate General Authority — Tokenization API",
    description:
      "Connect to the Real Estate Registry (RER) to tokenize properties, verify ownership deeds, and register tokens on the national blockchain.",
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
        <circle cx="19" cy="6" r="3" />
        <path d="M17.5 6h3M19 4.5v3" />
      </svg>
    ),
    accentColor: "#1e40af",
    path: "/admin/integrations/rer",
    provider: "Real Estate General Authority (REGA)",
    docUrl: "https://rega.gov.sa/",
  },
  virtual_iban: {
    title: "Virtual IBAN",
    subtitle: "Alinma Bank — Payment Collection",
    description:
      "Generate virtual IBANs for each investor to enable direct bank transfers for property token purchases. Funds are automatically reconciled.",
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
        <line x1="6" y1="15" x2="10" y2="15" />
        <line x1="14" y1="15" x2="18" y2="15" />
      </svg>
    ),
    accentColor: "#7c3aed",
    path: "/admin/integrations/virtual-iban",
    provider: "Alinma Bank",
    docUrl: "https://www.alinma.com/",
  },
  zatca: {
    title: "ZATCA",
    subtitle: "Zakat, Tax and Customs Authority — e-Invoicing",
    description:
      "Connect to ZATCA's Fatoora platform to submit ZATCA Phase 2 compliant e-invoices. Required for VAT reporting and clearance of all platform fee invoices.",
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
    accentColor: "#b45309",
    path: "/admin/integrations/zatca",
    provider: "Zakat, Tax and Customs Authority (ZATCA)",
    docUrl: "https://zatca.gov.sa/en/E-Invoicing/Pages/default.aspx",
  },
};

const IntegrationCard = ({ integration, meta }) => {
  const status = STATUS_COLORS[integration?.status || "inactive"];
  const lastTested = integration?.lastTested
    ? new Date(integration.lastTested).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})
    : null;

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.2s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.10)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)")}
    >
      {/* Accent bar */}
      <div style={{ height: 4, background: meta.accentColor, borderRadius: "14px 14px 0 0" }} />

      <div style={{ padding: "24px 24px 20px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 52, height: 52, borderRadius: 12,
                background: `${meta.accentColor}12`,
                color: meta.accentColor,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {meta.icon}
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#111827" }}>{meta.title}</h3>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>{meta.subtitle}</p>
            </div>
          </div>
          {/* Status badge */}
          <span
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 10px", borderRadius: 20,
              background: status.bg, color: status.text,
              fontSize: 11, fontWeight: 600,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: status.dot, display: "inline-block" }} />
            {status.label}
          </span>
        </div>

        {/* Description */}
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#4b5563", lineHeight: 1.6 }}>
          {meta.description}
        </p>

        {/* Provider */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
          <svg width="13" height="13" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          <span style={{ fontSize: 12, color: "#6b7280" }}>Provider: <strong>{meta.provider}</strong></span>
        </div>

        {/* Last tested */}
        {lastTested && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
            <svg width="13" height="13" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              Last tested: <strong>{lastTested}</strong>
              {integration?.lastTestResult?.success === true && (
                <span style={{ color: "#10b981", marginLeft: 6 }}>✓ Passed</span>
              )}
              {integration?.lastTestResult?.success === false && integration?.lastTestResult?.message && (
                <span style={{ color: "#f59e0b", marginLeft: 6 }}>⚠ Pending</span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: "auto", padding: "14px 24px",
          borderTop: "1px solid #f3f4f6",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#fafafa",
        }}
      >
        <a
          href={meta.docUrl}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 12, color: "#6b7280", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}
        >
          <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
          </svg>
          Provider Docs
        </a>
        <Link
          to={meta.path}
          style={{
            padding: "8px 18px",
            background: meta.accentColor,
            color: "#fff",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: "none",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          Configure
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </Link>
      </div>
    </div>
  );
};

const Integrations = () => {
  const { auth } = useSelector((state) => state);
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        const token =
          auth?.data?.accesstoken || auth?.data?.access_token;
        const res = await getDataAPIAuth("admin/integrations", token);
        if (res.data.status === 1) {
          const map = {};
          res.data.data.forEach((item) => {
            map[item.key] = item;
          });
          setIntegrations(map);
        }
      } catch (err) {
        console.error("Failed to fetch integrations", err);
      } finally {
        setLoading(false);
      }
    };
    if (auth?.data?.accesstoken || auth?.data?.access_token) {
      fetchIntegrations();
    }
  }, [auth]);

  const activeCount = Object.values(integrations).filter((i) => i.status === "active").length;
  const sandboxCount = Object.values(integrations).filter((i) => i.status === "sandbox").length;

  return (
    <AdminShell pageTitle="Integrations">
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#111827" }}>
          Third-Party Integrations
        </h2>
        <p style={{ margin: "6px 0 0", fontSize: 14, color: "#6b7280" }}>
          Manage credentials and connectivity for Nafath, RER, Virtual IBAN, and ZATCA e-Invoicing services.
        </p>
      </div>

      {/* Summary stats */}
      <div
        style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 32,
        }}
      >
        {[
          { label: "Total Integrations", value: 4, color: "#111827" },
          { label: "Active", value: activeCount, color: "#10b981" },
          { label: "Sandbox / Testing", value: sandboxCount, color: "#f59e0b" },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb",
              padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>{stat.label}</span>
            <span style={{ fontSize: 24, fontWeight: 800, color: stat.color }}>{stat.value}</span>
          </div>
        ))}
      </div>

      {/* Integration cards */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af", fontSize: 14 }}>
          Loading integrations…
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 24 }}>
          {Object.entries(INTEGRATION_META).map(([key, meta]) => (
            <IntegrationCard
              key={key}
              integration={integrations[key]}
              meta={meta}
            />
          ))}
        </div>
      )}

      {/* Info banner */}
      <div
        style={{
          marginTop: 32, padding: "16px 20px",
          background: "#eff6ff", borderRadius: 10, border: "1px solid #bfdbfe",
          display: "flex", alignItems: "flex-start", gap: 12,
        }}
      >
        <svg width="18" height="18" fill="none" stroke="#3b82f6" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }}>
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1e40af" }}>Integrations are ready to configure</p>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#3b82f6" }}>
            All credentials are stored securely in the database and never exposed to the frontend.
            Configure each integration once you receive the API credentials from the respective provider.
          </p>
        </div>
      </div>
    </AdminShell>
  );
};

export default Integrations;
